import { styled } from "goober";
import { useState } from "react";
import { mediaTypeFromFileName } from "../../../shared/fileMediaTypes";
import type { ChatOutputView } from "../../tools";
import { useToolOutput } from "../../hooks/useToolOutput";
import { formatBytes } from "../../lib/formatBytes";
import { mediaTypeLabel } from "../../lib/mediaTypeLabel";
import { parseChatOutputView } from "../../tools/ShowOutput";
import { CountBadge } from "../CountBadge";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { StepDetails } from "../StepDetails";
import { Text } from "../Text";
import {
  ChatpadBody,
  ChatpadList,
  ChatpadListBadge,
  ChatpadListHeader,
  ChatpadListRow,
  ChatpadListScrollArea,
} from "./ChatpadList";
import { type ChatOutput, iconForFileName } from "./chatOutputs";

const Thumbnail = styled("img")`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
`;

function OutputFileRow({
  output,
  onShowOutput,
}: {
  output: ChatOutput;
  onShowOutput: (output: ChatOutputView) => void;
}) {
  const [failedThumbnailUrl, setFailedThumbnailUrl] = useState<string>();
  const { output: result } = useToolOutput(output.entryId, true);
  const parsed = parseChatOutputView(output.toolName, result);
  const metadata = parsed?.key === output.key ? parsed : null;
  const inferredType = mediaTypeFromFileName(output.fileName);
  const mediaType =
    metadata?.mediaType && metadata.mediaType !== "application/octet-stream"
      ? metadata.mediaType
      : inferredType;
  const subtitle = [
    mediaTypeLabel(mediaType),
    metadata && formatBytes(metadata.sizeBytes),
  ]
    .filter(Boolean)
    .join(" · ");
  const thumbnailUrl = mediaType.startsWith("image/")
    ? output.thumbnailUrl
    : undefined;

  return (
    <ChatpadListRow
      title={output.fileName}
      description={subtitle}
      badge={
        <ChatpadListBadge>
          {thumbnailUrl && thumbnailUrl !== failedThumbnailUrl ? (
            <Thumbnail
              src={thumbnailUrl}
              alt=""
              crossOrigin="use-credentials"
              loading="lazy"
              decoding="async"
              onError={() => setFailedThumbnailUrl(thumbnailUrl)}
            />
          ) : (
            <Icon name={iconForFileName(output.fileName)} size={16} />
          )}
        </ChatpadListBadge>
      }
      onPress={() =>
        onShowOutput({
          key: output.key,
          url: output.url,
          thumbnailUrl: output.thumbnailUrl,
          fileName: output.fileName,
          mediaType,
          sizeBytes: metadata?.sizeBytes ?? 0,
        })
      }
    />
  );
}

export function OutputsList({
  outputs,
  onShowOutput,
}: {
  outputs: ChatOutput[];
  onShowOutput: (output: ChatOutputView) => void;
}) {
  return (
    <StepDetails
      embedded
      heading={null}
      flushBody
      body={
        outputs.length === 0 ? (
          <EmptyState
            iconName="tool-case"
            title="No work yet"
            description="Work from this chat will appear here."
          />
        ) : (
          <ChatpadBody data-fill="true">
            <ChatpadList role="region" aria-label="Work" data-fill="true">
              <ChatpadListHeader style={{ paddingInline: 16, gap: 8 }}>
                <Text variant="text-small-md">Work</Text>
                <CountBadge>
                  <Text variant="text-smaller-md">{outputs.length}</Text>
                </CountBadge>
              </ChatpadListHeader>
              <ChatpadListScrollArea>
                {outputs.map((output) => (
                  <OutputFileRow
                    key={output.key}
                    output={output}
                    onShowOutput={onShowOutput}
                  />
                ))}
              </ChatpadListScrollArea>
            </ChatpadList>
          </ChatpadBody>
        )
      }
    />
  );
}
